/**
 * Per-tab plan ownership (KOA-17797).
 *
 * A task plan (plan-mode `PlanDocument`, serialized as `planJson`) belongs to
 * exactly ONE chat tab. Without an owner stamp a plan created in one tab can
 * bleed into another — via a save/restore race or a request carrying a stale
 * plan — and the server's planner then injects it as the session's "standing
 * plan" into an UNRELATED turn, hijacking the request. Observed live: an
 * "az önceki tasarım diline uygun yap" restyle on a fresh LoginForm turned into
 * a full product-management build because a plan from an earlier tab leaked in.
 *
 * Fix: stamp the owning chat-tab id into the plan the moment the client first
 * receives it, and refuse to send/restore a plan whose owner differs from the
 * current tab. The stamp lives INSIDE the JSON (no DB schema change) and
 * round-trips through the server untouched. Legacy plans with no stamp are
 * treated as owned (one-time transition) and get stamped on the next plan event.
 *
 * Malformed `planJson` is handled asymmetrically by design: `stampPlanOwner`
 * passes it through (we don't drop a value we can't parse), but `planBelongsToTab`
 * / `planJsonForTab` reject it. Net effect — a malformed plan can linger in tab
 * state yet is never sent or restored. Harmless dead value, not a leak.
 */

interface OwnedPlan {
  ownerTabId?: string;
}

/** Stamp `tabId` as the owner of the serialized plan. No-op on empty/malformed input. */
export function stampPlanOwner(planJson: string | null | undefined, tabId: string): string | null {
  if (!planJson) return null;
  try {
    const plan: OwnedPlan = JSON.parse(planJson);
    plan.ownerTabId = tabId;
    return JSON.stringify(plan);
  } catch {
    // Malformed JSON — pass through rather than dropping a value we can't parse.
    return planJson;
  }
}

/**
 * True when the plan has no owner stamp (legacy) or its owner matches `tabId`.
 * A plan stamped for a DIFFERENT tab returns false — it must not be sent or
 * restored for this tab.
 */
export function planBelongsToTab(planJson: string | null | undefined, tabId: string): boolean {
  if (!planJson) return false;
  try {
    const plan: OwnedPlan = JSON.parse(planJson);
    return plan.ownerTabId === undefined || plan.ownerTabId === tabId;
  } catch {
    return false;
  }
}

/** Restore-time filter: keep the plan only if it belongs to this tab, else drop it. */
export function planJsonForTab(planJson: string | null | undefined, tabId: string): string | null {
  return planBelongsToTab(planJson, tabId) ? (planJson ?? null) : null;
}
